import { useQuery, gql } from "@apollo/client";
import { Stack, Typography } from "@mui/material";
import SimpleTable from "../../../../../component/SimpleTable";
import { ShippingByNumber } from "../../query";
import TrackingInfo from "./TrackingInfo";
import GeneratePackingBtn from "./GeneratePackingBtn";
import PGShippingDocBtn from "./PGShippingDocBtn";
import { toChinaDateString } from "../../../../../lib/chinaDate";

const relationsByDefiner = /* GraphQL */ `
  query RelationsByDefiner(
    $definerID: ID!
    $relation: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelEntityRelationsFilterInput
    $limit: Int
    $nextToken: String
  ) {
    relationsByDefiner(
      definerID: $definerID
      relation: $relation
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        relation
        definerID
        entity1ID
        entity2ID
        entity2 {
          id
          name
          alias
          abbr @client
        }
        alias
        customerCode
        vendorCode
        logisticsAccountNumber
        logisticsTrackingTemplate
        statusCode
        type
        paymentTerms
        serviceFeeRate
        minCharge
        groupToRead
        groupToEdit
        groupToDelete
        createdAt
        updatedAt
        __typename
      }
      nextToken
      __typename
    }
  }
`;

const getEntityUserRelation = /* GraphQL */ `
  query GetEntityUserRelation($id: ID!) {
    getEntityUserRelation(id: $id) {
      id
      role
      title
      shownFn
      shownLn
      shownMn
      statusCode
      isEmailVerified
      userID
      user {
        id
        sub
        email
        statusCode
        fn
        mn
        ln
        name @client
        phoneNumber
        address
        groupToRead
        groupToEdit
        groupToDelete
        owner
        definerID
        isDeleted
        createdAt
        updatedAt
        __typename
      }
      entityID
      entity {
        id
        isVerified
        name
        alias
        address
        phoneNumber
        type
        businessType
        statusCode
        paymentTerms
        groupToRead
        groupToEdit
        groupToDelete
        definerID
        isDeleted
        createdAt
        updatedAt
        __typename
      }
      definerID

      groupToRead
      groupToEdit
      groupToDelete
      createdAt
      updatedAt
      __typename
    }
  }
`;

export default function Packing({ number, definerID }) {
  const { data } = useQuery(gql(ShippingByNumber), {
    variables: { number },
    skip: !number,
  });

  const { data: courierRelationData } = useQuery(gql(relationsByDefiner), {
    variables: { definerID, relation: { eq: "PRINCIPAL-CONTRACTOR" } },
  });

  const shippingItems = data?.ShippingByNumber?.items?.map((item, i) => {
    return {
      id: item?.id,
      poNumber: item?.po?.no,
      itemNumber: item?.itemNumber,
      qty: item?.qty,
      coo: item?.coo?.toUpperCase(),
      mpn: item?.material?.mpn,
      mfr: item?.material?.mfr,
      unit: item?.material?.unit,
      code: item?.materialEntityRelation?.code,
      poID: item?.po?.id,
      materialID: item?.material?.id,
      number: item?.itemNumber * 1 === 1 ? number : "",
      specifications: item?.material?.specifications,
      trackingNumber: item?.trackingNumber,
      courierID: item?.courierID,
      po: item?.po,
      packingDetails: item.packingDetails,
    };
  });

  const invoices = data?.ShippingByNumber?.items
    ?.map((x) =>
      x.invoice.items.map((y) => ({
        ...y,
        po: x.po,
        materialEntityRelation: x.materialEntityRelation,
        material: x.material,
      }))
    )
    .flat()
    .map((x) => ({
      id: x.id,
      date: x.date,
      category: x.category,
      number: x.number,
      itemNumber: x.itemNumber,
      code: x.materialEntityRelation?.code || "N/A",
      desc: x?.desc || x.material?.mpn,
      poNumber: x.po?.no,
      qty: x.qty,
      price: x.price,
      currency: x.currency,
      unit: x.material?.unit,
    }));

  const { vendor, customer, incoTerms, paymentTerms } =
    shippingItems?.[0]?.po || {};

  const { entityUserRelationID } = incoTerms || {};

  const { data: entityUserRelationData } = useQuery(
    gql(getEntityUserRelation),
    {
      variables: { id: entityUserRelationID },
      skip: !entityUserRelationID,
    }
  );

  const { user: receiver, entity: receiverEntity } =
    entityUserRelationData?.getEntityUserRelation || {};

  if (!data) return null;

  const invoiceHeaders = [
    "number",
    "itemNumber",
    "code",
    "desc",
    "unit",
    "date",
    "category",
    "poNumber",
    "qty",
    "price",
    "currency",
  ];

  const packingHeaders = [
    "poNumber",
    "ctnFrom",
    "ctnTo",
    "gcas",
    "mfr",
    "mpn",
    "coo",
    "qty",
    "unit",
    "l",
    "w",
    "h",
    "gw",
    "nw",
    "dimensionUnit",
    "weightUnit",
  ];

  const metaLine = data?.ShippingByNumber?.items?.find(
    (x) => x.itemNumber * 1 === 1
  );

  const { date: rawDate, packingDetails, courier, trackingNumber } = metaLine || {};
  const date = toChinaDateString(rawDate);

  const courierRelation = courierRelationData?.relationsByDefiner?.items?.find(
    (x) => x.entity2ID === courier?.id
  );

  const logisticsTrackingTemplate = courierRelation?.logisticsTrackingTemplate;

  const trackingURL = logisticsTrackingTemplate?.replace(
    "TRACKINGNUMBER",
    trackingNumber
  );

  // console.log({ logisticsTrackingTemplate, trackingNumber, trackingURL });

  // console.log({ shippingItems });

  // console.log({ metaLine, date, packingDetails, courier, trackingNumber });

  // const { packingDetails, carrier, trackingNumber } =
  //   data?.ShippingByNumber?.items?.find((x) => x.itemNumber * 1 === 1) || {};

  return (
    <Stack
      spacing={2}
      justifyContent="space-between"
      sx={{ minHeight: 120 }}
      bgcolor="#f1f1f1"
      borderRadius={1}
      padding={2}
    >
      <Stack spacing={3}>
        <Stack spacing={1}>
          <Typography variant="h6" sx={{ alignSelf: "center" }}>
            Packing And Invoicing
          </Typography>
          <Typography variant="body" sx={{ alignSelf: "center" }}>
            Receiver: {receiver?.name}
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="space-between">
            {/* <Typography variant='body2' sx={{ alignSelf: 'center' }}>
              TOTAL: {shippingItems?.reduce((a, v) => a + (v.qty || 0), 0)}
            </Typography> */}
            <TrackingInfo shippingItems={shippingItems} definerID={definerID} />
          </Stack>
        </Stack>
        <Typography variant="h5">Invoicing</Typography>
        <SimpleTable headers={invoiceHeaders} rows={invoices} />
        <Typography variant="h5">Packing</Typography>
        <SimpleTable headers={packingHeaders} rows={packingDetails} />

        <Stack justifyContent="center" direction="row" spacing={2}>
          <GeneratePackingBtn
            shippingItems={shippingItems}
            packingDetails={packingDetails}
          />
          <PGShippingDocBtn
            shippingNumber={number}
            shippingItems={shippingItems}
            packingDetails={packingDetails}
            invoices={invoices}
            date={date}
            courier={courier}
            trackingNumber={trackingNumber}
            trackingURL={trackingURL}
            receiver={receiver}
            receiverEntity={receiverEntity}
            type="prev"
          />
          <PGShippingDocBtn
            shippingNumber={number}
            shippingItems={shippingItems}
            packingDetails={packingDetails}
            invoices={invoices}
            date={date}
            courier={courier}
            trackingNumber={trackingNumber}
            trackingURL={trackingURL}
            receiver={receiver}
            receiverEntity={receiverEntity}
            type="to Agent"
          />
          <PGShippingDocBtn
            shippingNumber={number}
            shippingItems={shippingItems}
            packingDetails={packingDetails}
            invoices={invoices}
            receiver={receiver}
            receiverEntity={receiverEntity}
            date={date}
            courier={courier}
            trackingNumber={trackingNumber}
            trackingURL={trackingURL}
            type="to Customer"
          />
        </Stack>
      </Stack>
    </Stack>
  );
}
